import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Save, Send, Server, Mail, User, Lock, CheckCircle, XCircle, Loader2, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminEmailSettingsPage = () => {
  const { getAuthHeaders } = useApp();
  const [loading, setLoading] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingIncoming, setSavingIncoming] = useState(false);
  const [savingOutgoing, setSavingOutgoing] = useState(false);
  const [testingIncoming, setTestingIncoming] = useState(false);
  const [testingOutgoing, setTestingOutgoing] = useState(false);
  
  // Status
  const [incomingStatus, setIncomingStatus] = useState(null); // null, 'checking', 'connected', 'error'
  const [outgoingStatus, setOutgoingStatus] = useState(null);
  
  const [settings, setSettings] = useState({
    // Account
    email_address: '',
    username: '',
    password: '',
    // Incoming (IMAP)
    imap_server: '',
    imap_security: 'SSL',
    imap_port: 993,
    // Outgoing (SMTP)
    smtp_server: '',
    smtp_security: 'SSL',
    smtp_port: 465,
    smtp_auth_required: true,
    smtp_from_name: 'Rentiasi.ro',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/email`, {
        headers: getAuthHeaders(),
      });
      
      // Map old fields to new structure
      setSettings({
        email_address: data.email_address || data.smtp_from_email || '',
        username: data.username || data.smtp_username || '',
        password: data.password || data.smtp_password || '',
        imap_server: data.imap_server || '',
        imap_security: data.imap_security || (data.imap_use_ssl ? 'SSL' : 'None'),
        imap_port: data.imap_port || (data.imap_ssl_port || 993),
        smtp_server: data.smtp_server || '',
        smtp_security: data.smtp_security || (data.smtp_use_ssl ? 'SSL' : 'None'),
        smtp_port: data.smtp_port || (data.smtp_ssl_port || 465),
        smtp_auth_required: data.smtp_auth_required !== false,
        smtp_from_name: data.smtp_from_name || 'Rentiasi.ro',
      });
      
      // Check statuses
      checkIncomingStatus();
      checkOutgoingStatus();
    } catch (error) {
      console.error('Failed to fetch email settings');
    } finally {
      setLoading(false);
    }
  };

  const saveAccountSettings = async () => {
    setSavingAccount(true);
    try {
      const payload = {
        ...settings,
        smtp_username: settings.username,
        smtp_password: settings.password,
        smtp_from_email: settings.email_address,
        imap_username: settings.username,
        imap_password: settings.password,
        smtp_use_ssl: settings.smtp_security === 'SSL',
        imap_use_ssl: settings.imap_security === 'SSL',
      };
      await axios.put(`${BACKEND_URL}/api/settings/email`, payload, {
        headers: getAuthHeaders(),
      });
      toast.success('Account settings saved!');
    } catch (error) {
      toast.error('Failed to save account settings');
    } finally {
      setSavingAccount(false);
    }
  };

  const saveIncomingSettings = async () => {
    setSavingIncoming(true);
    try {
      const payload = {
        ...settings,
        smtp_username: settings.username,
        smtp_password: settings.password,
        smtp_from_email: settings.email_address,
        imap_username: settings.username,
        imap_password: settings.password,
        smtp_use_ssl: settings.smtp_security === 'SSL',
        imap_use_ssl: settings.imap_security === 'SSL',
      };
      await axios.put(`${BACKEND_URL}/api/settings/email`, payload, {
        headers: getAuthHeaders(),
      });
      toast.success('Incoming server settings saved!');
      checkIncomingStatus();
    } catch (error) {
      toast.error('Failed to save incoming settings');
    } finally {
      setSavingIncoming(false);
    }
  };

  const saveOutgoingSettings = async () => {
    setSavingOutgoing(true);
    try {
      const payload = {
        ...settings,
        smtp_username: settings.username,
        smtp_password: settings.password,
        smtp_from_email: settings.email_address,
        imap_username: settings.username,
        imap_password: settings.password,
        smtp_use_ssl: settings.smtp_security === 'SSL',
        imap_use_ssl: settings.imap_security === 'SSL',
      };
      await axios.put(`${BACKEND_URL}/api/settings/email`, payload, {
        headers: getAuthHeaders(),
      });
      toast.success('Outgoing server settings saved!');
      checkOutgoingStatus();
    } catch (error) {
      toast.error('Failed to save outgoing settings');
    } finally {
      setSavingOutgoing(false);
    }
  };

  const checkIncomingStatus = async () => {
    if (!settings.imap_server || !settings.username) {
      setIncomingStatus(null);
      return;
    }
    
    setIncomingStatus('checking');
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/email/status/imap`, {
        headers: getAuthHeaders(),
      });
      setIncomingStatus(data.connected ? 'connected' : 'error');
    } catch (error) {
      setIncomingStatus('error');
    }
  };

  const checkOutgoingStatus = async () => {
    if (!settings.smtp_server || !settings.username) {
      setOutgoingStatus(null);
      return;
    }
    
    setOutgoingStatus('checking');
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/email/status`, {
        headers: getAuthHeaders(),
      });
      setOutgoingStatus(data.connected ? 'connected' : 'error');
    } catch (error) {
      setOutgoingStatus('error');
    }
  };

  const testIncoming = async () => {
    setTestingIncoming(true);
    setIncomingStatus('checking');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/settings/email/test/imap`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success(data.message || 'IMAP connection successful!');
      setIncomingStatus('connected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'IMAP connection failed');
      setIncomingStatus('error');
    } finally {
      setTestingIncoming(false);
    }
  };

  const testOutgoing = async () => {
    setTestingOutgoing(true);
    setOutgoingStatus('checking');
    try {
      // First just test the connection
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/email/status`, {
        headers: getAuthHeaders(),
      });
      if (data.connected) {
        toast.success('SMTP connection successful!');
        setOutgoingStatus('connected');
      } else {
        toast.error(data.error || 'SMTP connection failed');
        setOutgoingStatus('error');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP connection failed');
      setOutgoingStatus('error');
    } finally {
      setTestingOutgoing(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const getStatusBadge = (status, type) => {
    switch (status) {
      case 'checking':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
            <Loader2 size={14} className="animate-spin" />
            Checking...
          </span>
        );
      case 'connected':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
            <CheckCircle size={14} />
            Connected
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
            <XCircle size={14} />
            Failed
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">
            Not tested
          </span>
        );
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-email-settings-page">
        <div className="mb-8">
          <h1 className="text-4xl font-serif">Email Server</h1>
          <p className="text-muted-foreground mt-2">Configure IMAP and SMTP settings for email</p>
        </div>

        {/* Overall Status */}
        <div className="glass p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <ArrowDownToLine size={18} className="text-blue-400" />
              <span className="text-sm text-muted-foreground">Incoming (IMAP):</span>
              {getStatusBadge(incomingStatus, 'imap')}
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpFromLine size={18} className="text-green-400" />
              <span className="text-sm text-muted-foreground">Outgoing (SMTP):</span>
              {getStatusBadge(outgoingStatus, 'smtp')}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Account Settings */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <User size={24} className="text-primary" />
                <h2 className="text-xl font-serif">Account</h2>
              </div>
              <Button
                onClick={saveAccountSettings}
                disabled={savingAccount}
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={16} className="mr-2" />
                {savingAccount ? 'Saving...' : 'Save Account'}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Email Address</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={settings.email_address}
                    onChange={(e) => updateSetting('email_address', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-transparent border border-white/20 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Username</label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={settings.username}
                    onChange={(e) => updateSetting('username', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-transparent border border-white/20 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="password"
                    value={settings.password}
                    onChange={(e) => updateSetting('password', e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-transparent border border-white/20 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Incoming Server (IMAP) */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ArrowDownToLine size={24} className="text-blue-400" />
                <div>
                  <h2 className="text-xl font-serif">Incoming Server (IMAP)</h2>
                  <p className="text-xs text-muted-foreground">For receiving emails</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(incomingStatus, 'imap')}
                <Button
                  onClick={testIncoming}
                  disabled={testingIncoming || !settings.imap_server}
                  variant="outline"
                  size="sm"
                  className="border-blue-400 text-blue-400 hover:bg-blue-400/10"
                >
                  {testingIncoming ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                  Test
                </Button>
                <Button
                  onClick={saveIncomingSettings}
                  disabled={savingIncoming}
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Save size={16} className="mr-2" />
                  {savingIncoming ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">IMAP Server</label>
                <input
                  type="text"
                  value={settings.imap_server}
                  onChange={(e) => updateSetting('imap_server', e.target.value)}
                  placeholder="hosting2216152.online.pro"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Security Type</label>
                <select
                  value={settings.imap_security}
                  onChange={(e) => updateSetting('imap_security', e.target.value)}
                  className="w-full bg-slate-900 border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                >
                  <option value="SSL">SSL</option>
                  <option value="TLS">TLS</option>
                  <option value="STARTTLS">STARTTLS</option>
                  <option value="None">None</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Port</label>
                <input
                  type="number"
                  value={settings.imap_port}
                  onChange={(e) => updateSetting('imap_port', parseInt(e.target.value))}
                  placeholder="993"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                />
                <p className="text-xs text-muted-foreground mt-1">SSL: 993, TLS/None: 143</p>
              </div>
            </div>
          </div>

          {/* Outgoing Server (SMTP) */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ArrowUpFromLine size={24} className="text-green-400" />
                <div>
                  <h2 className="text-xl font-serif">Outgoing Server (SMTP)</h2>
                  <p className="text-xs text-muted-foreground">For sending emails</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(outgoingStatus, 'smtp')}
                <Button
                  onClick={testOutgoing}
                  disabled={testingOutgoing || !settings.smtp_server}
                  variant="outline"
                  size="sm"
                  className="border-green-400 text-green-400 hover:bg-green-400/10"
                >
                  {testingOutgoing ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                  Test
                </Button>
                <Button
                  onClick={saveOutgoingSettings}
                  disabled={savingOutgoing}
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Save size={16} className="mr-2" />
                  {savingOutgoing ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">SMTP Server</label>
                <input
                  type="text"
                  value={settings.smtp_server}
                  onChange={(e) => updateSetting('smtp_server', e.target.value)}
                  placeholder="hosting2216152.online.pro"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Security Type</label>
                <select
                  value={settings.smtp_security}
                  onChange={(e) => updateSetting('smtp_security', e.target.value)}
                  className="w-full bg-slate-900 border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                >
                  <option value="SSL">SSL</option>
                  <option value="TLS">TLS</option>
                  <option value="STARTTLS">STARTTLS</option>
                  <option value="None">None</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Port</label>
                <input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => updateSetting('smtp_port', parseInt(e.target.value))}
                  placeholder="465"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                />
                <p className="text-xs text-muted-foreground mt-1">SSL: 465, TLS/STARTTLS: 587</p>
              </div>
            </div>

            {/* Additional SMTP Options */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    id="smtp_auth"
                    checked={settings.smtp_auth_required}
                    onChange={(e) => updateSetting('smtp_auth_required', e.target.checked)}
                    className="w-5 h-5 rounded border-white/20 bg-transparent accent-primary"
                  />
                  <label htmlFor="smtp_auth" className="text-sm">
                    Require authentication to send emails
                  </label>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">From Name</label>
                  <input
                    type="text"
                    value={settings.smtp_from_name}
                    onChange={(e) => updateSetting('smtp_from_name', e.target.value)}
                    placeholder="Rentiasi.ro"
                    className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary rounded"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Info */}
          <div className="glass p-6 bg-slate-900/50">
            <h3 className="text-lg font-semibold mb-4 text-primary">Server Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-slate-800/50 rounded">
                <p className="text-muted-foreground mb-1">Current Server</p>
                <p className="font-mono">{settings.smtp_server || 'Not configured'}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <p className="text-muted-foreground mb-1">Email Account</p>
                <p className="font-mono">{settings.email_address || 'Not configured'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailSettingsPage;
